/**
 * formatCurrency.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Shared utility for formatting numbers, currencies, and quantities
 * with comma-separated thousands across the application.
 *
 * Usage:
 *   import { formatCurrency, formatNumber, getCurrencySymbol } from '../utils/formatCurrency';
 */

/**
 * Returns the currency symbol for a given ISO 4217 currency code.
 */
export function getCurrencySymbol(currencyCode: string): string {
  const symbols: Record<string, string> = {
    USD: '$',   EUR: '€',   GBP: '£',   JPY: '¥',   CAD: 'C$',
    AUD: 'A$',  CNY: '¥',   INR: '₹',   BRL: 'R$',  MXN: '$',
    SGD: 'S$',  HKD: 'HK$', CHF: 'CHF', KRW: '₩',   RUB: '₽',
    ZAR: 'R',   NZD: 'NZ$', SEK: 'kr',  NOK: 'kr',  DKK: 'kr',
    PLN: 'zł',  TRY: '₺',   THB: '฿',   IDR: 'Rp',  MYR: 'RM',
    PHP: '₱',   VND: '₫',   LKR: 'Rs',
  };
  return symbols[currencyCode] || currencyCode;
}

/**
 * Formats a numeric value as a locale string with thousands separators
 * and a fixed number of decimal places (default 2).
 *
 * Examples:
 *   formatNumber(1234567.5)    → "1,234,567.50"
 *   formatNumber(1000, 0)      → "1,000"
 *   formatNumber('9876.1', 2)  → "9,876.10"
 */
export function formatNumber(
  value: number | string,
  decimals: number = 2
): string {
  const n = typeof value === 'string' ? parseFloat(value) || 0 : value;
  return n.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Formats a numeric value as a currency string with the given symbol,
 * thousands separators, and 2 decimal places.
 *
 * Examples (symbol = "Rs"):
 *   formatCurrency(1234567.5, "Rs")  → "Rs 1,234,567.50"
 *   formatCurrency("500", "$")       → "$ 500.00"
 */
export function formatCurrency(
  amount: number | string,
  currencySymbol: string
): string {
  return `${currencySymbol} ${formatNumber(amount, 2)}`;
}

/**
 * Formats a price for display — identical to formatCurrency but named
 * to mirror the previous `formatPriceDisplay` helper for easy migration.
 */
export function formatPriceDisplay(
  price: number | string,
  currencySymbol: string
): string {
  return formatCurrency(price, currencySymbol);
}

/**
 * Formats an integer quantity with thousands separators (no decimals).
 *
 * Examples:
 *   formatQuantity(1234)  → "1,234"
 *   formatQuantity(50)    → "50"
 */
export function formatQuantity(value: number | string): string {
  return formatNumber(value, 0);
}